import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShareImageDialog, ShareImageService } from '@colonist/clientforge';
import { AnalyticEventCategory, AnalyticEventType, AnalyticEventValue, MobileImageSharingAnalytics } from '../../../../../shared/entity_datas/EntityDatas.ts';
import { uiManagerEvents } from '../../EntryShared.ts';
import { UIDeviceController } from '../../ui_html/UIDeviceController.ts';
import { showSimpleDialog } from '../showSimpleDialog.ts';
import { clientI18n } from '../../ClientI18n.ts';
import { UIUrlParameterNames } from '../../ui_html/UIUrlController.ts';
import { CA } from '../../analytics/Analytics.ts';
import { createInitialShareImageFooter, getShareImagePlatform } from './createShareImageDialogSetup.ts';
import { useCopyButtonFeedback } from './useCopyButtonFeedback.ts';
import { runWithClickSound } from '../../ui_utils/AudioUtils.ts';
export const ShareImageDialogContainer = ({ open, onClose, images, onReplayShare })=>{
    const [imageStates, setImageStates] = useState([]);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const platform = getShareImagePlatform();
    const hasReplayShare = onReplayShare != undefined;
    const [footer, setFooter] = useState(()=>createInitialShareImageFooter(platform, {
            hasReplayShare: hasReplayShare
        }));
    const { resetCopyButtonState, setCopyResult } = useCopyButtonFeedback(footer, setFooter);
    const handleStateChange = useCallback((nextImageStates)=>setImageStates(nextImageStates), []);
    const service = useMemo(()=>new ShareImageService(handleStateChange, images), [
        handleStateChange,
        images
    ]);
    useEffect(()=>{
        setSelectedImageIndex(0);
        service.start();
        return ()=>service.dispose();
    }, [
        service
    ]);
    const sendShareAchievementAnalytic = useCallback(()=>{
        var _imageStates_selectedImageIndex;
        const achievementEnum = (_imageStates_selectedImageIndex = imageStates[selectedImageIndex]) === null || _imageStates_selectedImageIndex === void 0 ? void 0 : _imageStates_selectedImageIndex.achievementEnum;
        if (achievementEnum == undefined) return;
        const shareTitleEvent = {
            type: AnalyticEventType.EndGameShareTitle,
            category: AnalyticEventCategory.Virality,
            floatValue: achievementEnum
        };
        void CA.sendAnalyticEventOnce(shareTitleEvent);
    }, [
        imageStates,
        selectedImageIndex
    ]);
    const runFooterAction = async (action)=>{
        switch(action){
            case 'copy':
                var _imageStates_selectedImageIndex;
                const copyImageFile = await ((_imageStates_selectedImageIndex = imageStates[selectedImageIndex]) === null || _imageStates_selectedImageIndex === void 0 ? void 0 : _imageStates_selectedImageIndex.getImageFile());
                await runCopyImageAction(copyImageFile, setCopyResult, sendShareAchievementAnalytic);
                break;
            case 'download':
                var _imageStates_selectedImageIndex1;
                const downloadImageFile = await ((_imageStates_selectedImageIndex1 = imageStates[selectedImageIndex]) === null || _imageStates_selectedImageIndex1 === void 0 ? void 0 : _imageStates_selectedImageIndex1.getImageFile());
                await runDownloadImageAction(downloadImageFile, sendShareAchievementAnalytic, onClose);
                break;
            case 'shareReplay':
                await (onReplayShare === null || onReplayShare === void 0 ? void 0 : onReplayShare());
                break;
            case 'share':
                var _imageStates_selectedImageIndex2;
                const shareImageFile = await ((_imageStates_selectedImageIndex2 = imageStates[selectedImageIndex]) === null || _imageStates_selectedImageIndex2 === void 0 ? void 0 : _imageStates_selectedImageIndex2.getImageFile());
                await runShareImageAction(platform, shareImageFile, sendShareAchievementAnalytic);
                break;
        }
    };
    const handleFooterAction = runWithClickSound(runFooterAction);
    const handleClose = runWithClickSound(onClose);
    const currentImageState = imageStates[selectedImageIndex];
    if (currentImageState == undefined) return null;
    const imageOptions = imageStates.map((imageState, index)=>({
            id: index,
            label: imageState.title
        }));
    const handleSelectImage = runWithClickSound((imageIndex)=>{
        setSelectedImageIndex(imageIndex);
        resetCopyButtonState();
    });
    return /*#__PURE__*/ React.createElement(ShareImageDialog, {
        open: open,
        onClose: handleClose,
        image: currentImageState.previewState,
        imageOptions: imageOptions,
        selectedImageId: selectedImageIndex,
        onSelectImage: handleSelectImage,
        footer: footer,
        onFooterAction: handleFooterAction
    });
};
const runCopyImageAction = async (imageFile, setCopyResult, sendShareAchievementAnalytic)=>{
    if (imageFile == undefined) return;
    const copyResult = await copyImageToClipboard(imageFile);
    setCopyResult(copyResult);
    sendCopyAnalytic();
    sendShareAchievementAnalytic();
};
const runDownloadImageAction = async (imageFile, sendShareAchievementAnalytic, onClose)=>{
    if (imageFile == undefined) return;
    downloadFile(imageFile);
    sendDownloadAnalytic();
    sendShareAchievementAnalytic();
    onClose();
};
const runShareImageAction = async (platform, imageFile, sendShareAchievementAnalytic)=>{
    if (imageFile == undefined) return;
    switch(platform){
        case 'desktop':
            return;
        case 'discord':
            runDiscordShareImageAction(imageFile, sendShareAchievementAnalytic);
            return;
        case 'mobile':
            await runMobileShareImageAction(imageFile, sendShareAchievementAnalytic);
            return;
    }
};
const runDiscordShareImageAction = (imageFile, sendShareAchievementAnalytic)=>{
    uiManagerEvents.shareImage(undefined, undefined, undefined, imageFile, undefined);
    sendDiscordShareAnalytic();
    sendShareAchievementAnalytic();
};
const runMobileShareImageAction = async (imageFile, sendShareAchievementAnalytic)=>{
    if (UIDeviceController.isUserOnFirefox()) {
        showSimpleDialog({
            title: {
                key: 'strings:popups.errors.sharingNotSupportedOnFirefox.title'
            },
            body: {
                key: 'strings:popups.errors.sharingNotSupportedOnFirefox.message'
            }
        });
        return;
    }
    const shareTitle = clientI18n.t('strings:game.endGame.shareImagePopup.title');
    const text = clientI18n.t('strings:homePage.popups.discordShareActivity.text');
    const mobileSharingAnalytics = MobileImageSharingAnalytics[MobileImageSharingAnalytics.GameResultsSharedFromGameEndScreen];
    const link = `${window.location.origin}/mobile#${UIUrlParameterNames.MobileImageSharing}=${mobileSharingAnalytics}`;
    const imageSrc = await fileToDataUrl(imageFile);
    if (imageSrc == undefined) return;
    uiManagerEvents.shareImage(shareTitle, text, link, imageFile, imageSrc);
    sendMobileShareAnalytic();
    sendShareAchievementAnalytic();
};
const copyImageToClipboard = async (file)=>{
    try {
        await navigator.clipboard.write([
            new ClipboardItem({
                [file.type]: file
            })
        ]);
        return 'success';
    } catch (error) {
        return 'failed';
    }
};
const fileToDataUrl = (file)=>{
    return new Promise((resolve)=>{
        const reader = new FileReader();
        reader.onload = ()=>resolve(reader.result);
        reader.onerror = ()=>resolve(undefined);
        reader.readAsDataURL(file);
    });
};
const downloadFile = (imageFile)=>{
    const url = URL.createObjectURL(imageFile);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = imageFile.name;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
};
const sendCopyAnalytic = ()=>{
    void CA.sendAnalyticEventOnce({
        type: AnalyticEventType.EndGameShareButton,
        category: AnalyticEventCategory.Virality,
        floatValue: AnalyticEventValue.EndGameShareCopyButton
    });
};
const sendDownloadAnalytic = ()=>{
    void CA.sendAnalyticEventOnce({
        type: AnalyticEventType.EndGameShareButton,
        category: AnalyticEventCategory.Virality,
        floatValue: AnalyticEventValue.EndGameShareDownloadButton
    });
};
const sendDiscordShareAnalytic = ()=>{
    void CA.sendAnalyticEventOnce({
        type: AnalyticEventType.EndGameShareButton,
        category: AnalyticEventCategory.Virality,
        floatValue: AnalyticEventValue.EndGameShareDiscordButton
    });
};
const sendMobileShareAnalytic = ()=>{
    void CA.sendAnalyticEventOnce({
        type: AnalyticEventType.EndGameShareButton,
        category: AnalyticEventCategory.Virality,
        floatValue: AnalyticEventValue.EndGameShareMobileButton
    });
};
